<template>
  <q-card class="fit re-mb">
    <q-card-section class="q-col-gutter-md">
      <div
        class="row q-pt-none q-pl-none"
        :style="{ width: 'calc(100% + 16px)' }"
      >
        <q-badge
          v-for="badge in visibleBadges"
          :key="badge.key"
          class="full-width no-border-radius text-center justify-center q-pa-xs"
          :color="badge.color"
          :text-color="badge.textColor"
          :multi-line="badge.multiLine"
        >
          {{ $t(badge.translationKey) }}
        </q-badge>
      </div>

      <div class="row justify-between items-center no-wrap">
        <h6 class="q-pl-sm text-dark">
          {{ props.row.displayAddress }}
        </h6>
        <q-chip
          v-if="shouldShowStatus"
          :color="statusChipColor(props.row.status)"
        >
          {{ $t(`status.${props.row.status}`) }}
        </q-chip>
      </div>
      <div class="row">
        <q-badge
          v-if="icon"
          color="dark-alpha-90"
          class="text-dark q-mr-sm"
        >
          <re-icon
            :name="icon"
            size="xs"
            color="primary"
          />

          <span class="q-ml-xs text-capitalize">
            {{ props.row.building_type ? $t(`value.${props.row.building_type}`) : '' }}
          </span>
        </q-badge>
        <q-badge
          v-if="currentValuationType"
          color="dark-alpha-90"
          class="text-dark items-baseline"
        >
          <re-icon
            :name="`ic-${currentValuationType.value}`"
            size="xs"
          />
          <span class="q-ml-xs text-capitalize self-center">{{ currentValuationType.label }}</span>
        </q-badge>
      </div>
      <div class="text-caption text-weight-medium text-dark">
        <span class="info-line-item">
          <span class="text-blue-grey">
            {{ $t('monolith.shared.request_ref') }}{{ $t('punctuation.:') }}&nbsp;
          </span>
          <span>{{ props.row.valuation_request_ref }}</span>
        </span>
      </div>
      <div class="tags-container">
        <cell-valuer
          v-if="hasRole('dispatcher')"
          v-bind="props"
          dense
        />
      </div>
      <div
        class="q-pl-none"
        :style="{ width: 'calc(100% + 16px)' }"
      >
        <q-separator class="q-my-sm q-pa-none q-pl-none" />
      </div>
      <div class="col-12">
        <action-buttons :row="props.row" />
      </div>
    </q-card-section>
  </q-card>
</template>
<script setup>
import { computed } from 'vue'

import ActionButtons from '@/apps/MainApp/views/Dashboard/ActionButtons/ActionButtons.vue'
import CellValuer from '@/apps/MainApp/views/Dashboard/TableCells/CellValuer.vue'
import { useOptionsFormatter } from '@/composables/optionsFormatter'
import { useCurUser } from '@/composables/useCurUser'
import enums from '@/plugins/enums.js'

const props = defineProps({
  row: {
    type: Object,
    required: true,
  },
})

const { hasRole } = useCurUser()
const { valuationTypeOptions, buildingTypeOptions } = useOptionsFormatter()

const icon = buildingTypeOptions.value.find((option) => option.value === props.row.building_type)?.icon
const badges = computed(() => props.row.badges)
const badgeConfigurations = [
  {
    key: 'resubmitted',
    condition: () => isTypeDispatcher.value || isValuer.value,
    color: 'warning-bground',
    textColor: 'warning',
    multiLine: true,
    translationKey: 'monolith.dashboard.badges.resubmitted',
  },
  {
    key: 'needs_borrower_email',
    condition: () => isTypeDispatcher.value || isOwner.value,
    color: 'warning',
    multiLine: true,
    translationKey: 'monolith.dashboard.badges.needs_borrower_email',
  },
  {
    key: 'needs_borrower_action',
    condition: () => isTypeDispatcher.value || isValuer.value || isOwner.value,
    color: 'warning',
    translationKey: 'monolith.dashboard.badges.needs_borrower_action',
  },
  {
    key: 'appointment_pending_confirmation',
    condition: () => isTypeDispatcher.value || isValuer.value,
    color: 'warning',
    multiLine: true,
    translationKey: 'monolith.dashboard.badges.needs_appointment_confirmation',
  },
  {
    key: 'appointment_confirmed',
    condition: () => isTypeDispatcher.value || isValuer.value || isOwner.value,
    color: 'positive',
    translationKey: 'monolith.dashboard.badges.appointment_confirmed',
  },
]

const visibleBadges = computed(() => {
  return badgeConfigurations.filter((badge) => badges.value.includes(badge.key) && badge.condition())
})

const isTypeDispatcher = computed(() => hasRole('dispatcher', props.row.valuation_type))
const isValuer = computed(() => props.row.valuer?.is_self)
const isOwner = computed(() => props.row.owner?.is_self)

const currentValuationType = computed(() => {
  return valuationTypeOptions.value.find((option) => option.value === props.row.valuation_type)
})

const statusChipColor = (status) => {
  switch (status) {
    case enums.requestStatus.AWAITING_ONSITE:
    case enums.requestStatus.DRAFT:
    case enums.requestStatus.OUT_OF_SCOPE:
      return enums.feedbackType.WARNING
    case enums.requestStatus.CANCELLED:
      return enums.feedbackType.NEGATIVE
    default:
      return enums.feedbackType.POSITIVE
  }
}

const shouldShowStatus = computed(() => {
  return props.row.requested_valuation_type !== enums.valuationTypes.CAT
})
</script>

<style lang="scss" scoped>
.info-line-separator {
  &:before {
    content: '•';
    color: var(--q-secondary);
  }
}

.tags-container div {
  margin-right: 1em;
}
</style>
